import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getData } from '../lib/api';

/**
 * Loads a public API endpoint for the current language.
 *
 * Returns the bundled `fallback` until (and unless) the request succeeds, so a
 * component using this hook renders its original static content when the
 * backend is absent.
 */
export default function useApi(path, { params, fallback = null, deps = [] } = {}) {
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  const [data, setData] = useState(fallback);
  const [loading, setLoading] = useState(true);
  const [fromApi, setFromApi] = useState(false);
  const mounted = useRef(true);

  const key = JSON.stringify({ path, params, language, deps });

  useEffect(() => {
    mounted.current = true;
    setLoading(true);

    (async () => {
      const result = await getData(path, {
        params: { lang: language, ...(params || {}) },
        fallback: null,
      });

      if (!mounted.current) return;

      if (result !== null && result !== undefined) {
        setData(result);
        setFromApi(true);
      } else {
        setFromApi(false);
      }

      setLoading(false);
    })();

    return () => {
      mounted.current = false;
    };
  }, [key]);

  return { data, loading, fromApi };
}
